import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Plus, Trash2, Copy, Check, ArrowLeft, LogOut, ExternalLink, RefreshCw } from "lucide-react";
import { useNoIndex } from "../lib/seoHelpers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * /admin/referrals — mint referral codes and track clicks/conversions.
 *
 * Admin flow:
 *   1. Click "New Code", pick a short code (e.g. AGENCY123), add referrer name/email.
 *   2. Share https://socialdisputeco.com/?ref=AGENCY123 with the referrer.
 *   3. Every visit to any site URL with ?ref=AGENCY123 logs a click.
 *   4. Every conversion (intake submit, purchase) tied to that code logs a conversion.
 *   5. Admin sees per-code counts here.
 */
export default function AdminReferralsPage() {
  useNoIndex();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [codes, setCodes] = useState([]);
  const [error, setError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [copiedCode, setCopiedCode] = useState("");

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("sdc_admin_token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/referrals`, { headers: getAuthHeaders() });
      setCodes(res.data);
      setError("");
    } catch (e) {
      if (e.response?.status === 401) {
        navigate("/admin");
        return;
      }
      setError("Failed to load referrals.");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, navigate]);

  useEffect(() => {
    if (!localStorage.getItem("sdc_admin_token")) {
      navigate("/admin");
      return;
    }
    load();
  }, [load, navigate]);

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete code ${code}? Event history is preserved for audit.`)) return;
    try {
      await axios.delete(`${API}/admin/referrals/${code}`, { headers: getAuthHeaders() });
      setCodes((prev) => prev.filter((c) => c.code !== code));
    } catch (_e) {
      alert("Delete failed.");
    }
  };

  const handleCopyUrl = async (code) => {
    const url = `https://socialdisputeco.com/?ref=${code}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(""), 2000);
    } catch (_e) {
      // Silent
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("sdc_admin_token");
    localStorage.removeItem("sdc_admin_user");
    navigate("/admin");
  };

  const totalClicks = codes.reduce((s, c) => s + (c.clicks || 0), 0);
  const totalConversions = codes.reduce((s, c) => s + (c.conversions || 0), 0);

  return (
    <div className="min-h-screen bg-[#1a1c1b]" data-testid="admin-referrals-page">
      {/* Header */}
      <header className="bg-[#252825] border-b border-[#3f4816]/50 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="text-[#888680] hover:text-[#d9fb06]">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-lg md:text-xl font-black text-[#d9fb06] uppercase">Referral Codes</h1>
              <p className="text-xs text-[#888680] font-mono">Mint · Share · Track clicks &amp; conversions</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="text-[#888680] hover:text-[#d9fb06] p-2" title="Refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={handleLogout} className="text-[#888680] hover:text-red-400 flex items-center gap-1.5 text-sm">
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <section className="bg-[#302f2c] border-b border-[#3f4816]/40">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 grid grid-cols-3 gap-4">
          <Stat label="Active Codes" value={codes.length} />
          <Stat label="Total Clicks" value={totalClicks} />
          <Stat label="Conversions" value={totalConversions} highlight />
        </div>
      </section>

      {/* Toolbar */}
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex justify-between items-center">
        <p className="text-sm text-[#dfddd6]">{codes.length} code{codes.length === 1 ? "" : "s"}</p>
        <button
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#d9fb06] text-[#1a1c1b] text-sm font-bold hover:bg-[#d9fb06]/90"
          data-testid="referrals-new-btn"
        >
          <Plus className="w-3.5 h-3.5" />
          New Code
        </button>
      </div>

      {error && (
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-2">
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300">{error}</div>
        </div>
      )}

      {/* Table */}
      <div className="max-w-7xl mx-auto px-5 md:px-10 pb-16">
        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#d9fb06] mx-auto mb-2" />
          </div>
        ) : codes.length === 0 ? (
          <div className="text-center py-16 rounded-xl border border-dashed border-[#3f4816]/50 bg-[#302f2c]/30">
            <p className="text-[#dfddd6] font-semibold mb-1">No referral codes yet.</p>
            <p className="text-sm text-[#888680]">Click &ldquo;New Code&rdquo; to mint your first one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-[#3f4816]/40">
            <table className="w-full">
              <thead className="bg-[#302f2c] text-[#888680] text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-3 font-mono">Code</th>
                  <th className="text-left px-4 py-3 font-mono hidden md:table-cell">Referrer</th>
                  <th className="text-right px-4 py-3 font-mono">Clicks</th>
                  <th className="text-right px-4 py-3 font-mono">Conversions</th>
                  <th className="text-right px-4 py-3 font-mono">Actions</th>
                </tr>
              </thead>
              <tbody className="text-sm text-[#dfddd6]">
                {codes.map((c) => (
                  <tr key={c.code} className="border-t border-[#3f4816]/30 hover:bg-[#302f2c]/50" data-testid={`code-row-${c.code}`}>
                    <td className="px-4 py-3 font-mono font-bold text-[#d9fb06]">{c.code}</td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <p className="text-[#dfddd6]">{c.referrerName}</p>
                      {c.referrerEmail && <p className="text-xs text-[#888680]">{c.referrerEmail}</p>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{c.clicks || 0}</td>
                    <td className="px-4 py-3 text-right font-mono text-[#d9fb06] font-bold">{c.conversions || 0}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleCopyUrl(c.code)}
                          className="text-[#888680] hover:text-[#d9fb06] p-1.5 rounded-md hover:bg-[#3f4816]/20"
                          title="Copy share URL"
                          data-testid={`copy-url-${c.code}`}
                        >
                          {copiedCode === c.code ? <Check className="w-4 h-4 text-[#d9fb06]" /> : <Copy className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleDelete(c.code)}
                          className="text-[#888680] hover:text-red-400 p-1.5 rounded-md hover:bg-[#3f4816]/20"
                          title="Delete"
                          data-testid={`delete-${c.code}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showNew && (
        <NewCodeModal
          onClose={() => setShowNew(false)}
          onCreated={(newCode) => {
            setCodes((prev) => [{ ...newCode, clicks: 0, conversions: 0 }, ...prev]);
            setShowNew(false);
          }}
          getAuthHeaders={getAuthHeaders}
        />
      )}
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`rounded-lg p-3 ${highlight ? "bg-[#d9fb06]/10 border border-[#d9fb06]/30" : "bg-[#1a1c1b] border border-[#3f4816]/40"}`}>
      <p className="text-xs uppercase tracking-widest text-[#888680] font-mono">{label}</p>
      <p className={`text-2xl font-black mt-1 ${highlight ? "text-[#d9fb06]" : "text-[#dfddd6]"}`}>{value}</p>
    </div>
  );
}

function NewCodeModal({ onClose, onCreated, getAuthHeaders }) {
  const [code, setCode] = useState("");
  const [referrerName, setReferrerName] = useState("");
  const [referrerEmail, setReferrerEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      const res = await axios.post(
        `${API}/admin/referrals`,
        { code, referrerName, referrerEmail: referrerEmail || undefined, notes: notes || undefined },
        { headers: getAuthHeaders() }
      );
      onCreated(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to create code.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="new-code-modal">
      <div className="bg-[#252825] border border-[#3f4816] rounded-2xl max-w-lg w-full p-6">
        <h3 className="text-lg font-black text-[#d9fb06] uppercase mb-4">New Referral Code</h3>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#888680] mb-1">
              Code <span className="text-red-400">*</span> <span className="text-[#888680] normal-case">(uppercase alphanumeric, e.g. AGENCY123)</span>
            </label>
            <input
              required
              maxLength={24}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
              className="w-full bg-[#1a1c1b] border border-[#3f4816]/50 rounded-lg px-3 py-2 text-[#d9fb06] font-mono font-bold focus:border-[#d9fb06] outline-none"
              data-testid="new-code-input"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#888680] mb-1">Referrer name <span className="text-red-400">*</span></label>
            <input
              required
              value={referrerName}
              onChange={(e) => setReferrerName(e.target.value)}
              className="w-full bg-[#1a1c1b] border border-[#3f4816]/50 rounded-lg px-3 py-2 text-sm text-[#dfddd6] focus:border-[#d9fb06] outline-none"
              data-testid="new-code-name"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#888680] mb-1">Referrer email</label>
            <input
              type="email"
              value={referrerEmail}
              onChange={(e) => setReferrerEmail(e.target.value)}
              className="w-full bg-[#1a1c1b] border border-[#3f4816]/50 rounded-lg px-3 py-2 text-sm text-[#dfddd6] focus:border-[#d9fb06] outline-none"
              data-testid="new-code-email"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#888680] mb-1">Notes</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full bg-[#1a1c1b] border border-[#3f4816]/50 rounded-lg px-3 py-2 text-sm text-[#dfddd6] focus:border-[#d9fb06] outline-none"
              data-testid="new-code-notes"
            />
          </div>
          {err && <p className="text-red-400 text-sm">{err}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-[#888680] hover:text-[#dfddd6]">Cancel</button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-[#d9fb06] text-[#1a1c1b] text-sm font-bold hover:bg-[#d9fb06]/90 disabled:opacity-50 flex items-center gap-1.5"
              data-testid="new-code-save-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
